import React, { useState, useRef, useEffect } from 'react';
import {
  Box, TextField, IconButton, Typography, Divider, Button,
} from '@mui/material';
import { Send, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { helpService } from '../../services/HelpService';
import HelpMessage from './HelpMessage';
import AiNotice from '../common/AiNotice';
import { isAppError, translateError } from '../../errors';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  confidence?: number;
  sources?: Array<{ file: string; section: string }>;
}

interface HelpChatProps {
  route: string;
}

const HelpChat: React.FC<HelpChatProps> = ({ route }) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('ec_help_chat_messages') || '[]');
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      sessionStorage.removeItem('ec_help_chat_messages');
    } else {
      sessionStorage.setItem('ec_help_chat_messages', JSON.stringify(messages));
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !accessToken || loading) return;

    const question = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setLoading(true);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const result = await helpService.sendMessage(accessToken, question, route, history);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.answer,
          confidence: result.confidence,
          sources: result.sources,
        },
      ]);
    } catch (err: any) {
      console.error('Help chat error:', err);
      let errorMessage = t('help.chatUnavailable');
      if (err?.status === 429) {
        // The per-user cap's own `help_rate_limit_exceeded` names the limit
        // and the window, so it wins. Matched by code rather than handed to
        // translateError: the IP limiter's 429 carries no code and arrives as
        // the `help_message_failed` fallback, which would read worse than the
        // generic sentence kept for it (TF-996).
        errorMessage = isAppError(err) && err.code === 'help_rate_limit_exceeded'
          ? translateError(err, t, 'help.rateLimited')
          : t('help.rateLimited');
      } else if (err?.status === 401 || err?.status === 403) {
        errorMessage = t('help.sessionExpired');
      }
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: errorMessage },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    sessionStorage.removeItem('ec_help_chat_messages');
    setMessages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <AiNotice kind="chat" sx={{ mb: 2 }} />
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            {t('help.chatPlaceholder')}
          </Typography>
        )}
        {messages.map((msg, i) => (
          <HelpMessage
            key={i}
            role={msg.role}
            content={msg.content}
            confidence={msg.confidence}
            sources={msg.sources}
            question={i > 0 ? messages[i - 1]?.content : undefined}
            route={route}
          />
        ))}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                animation: 'help-pulse 1.2s ease-in-out infinite',
                '@keyframes help-pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 0.6 },
                  '50%': { transform: 'scale(1.5)', opacity: 1 },
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('help.thinking', 'Denke nach…')}
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {messages.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
          <Button size="small" startIcon={<RestartAlt />} onClick={handleNewConversation}>
            {t('help.newConversation')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('help.chatPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          multiline
          maxRows={3}
        />
        <IconButton onClick={handleSend} disabled={!input.trim() || loading} color="primary">
          <Send />
        </IconButton>
      </Box>
    </Box>
  );
};

export default HelpChat;
